// React and Router
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";

// Firebase
import { auth } from "../../firebase.js";
import { onAuthStateChanged } from "firebase/auth";

// Components
import Header from "../shared/Header";

// Stripe
import getStripe from "../stripe/GetStripe.js";

// Styles
import "../../css/StorageManagement.css";

const API_BASE = "https://bluebirddocumentationadmin.pythonanywhere.com";

function StoragePlans() {
    const { uid } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [plans, setPlans] = useState([]);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [currentPlanQuantity, setCurrentPlanQuantity] = useState(1);
    const [storageUsedBytes, setStorageUsedBytes] = useState(0);
    const [error, setError] = useState(null);
    const [processingPlanId, setProcessingPlanId] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [currentUserUid, setCurrentUserUid] = useState(null);
    const [blockQuantities, setBlockQuantities] = useState({});
    const [portalLoading, setPortalLoading] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setIsLoggedIn(true);
                setCurrentUserUid(user.uid);
            } else {
                setIsLoggedIn(false);
                navigate("/login");
            }
        });
        return () => unsubscribe();
    }, [navigate]);

    const fetchPlans = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE}/storage/plans/`, {
                method: "GET",
                headers: { "Content-Type": "application/json" },
            });
            if (response.ok) {
                const data = await response.json();
                setPlans(data.plans || []);
            }
        } catch (error) {
            console.error("Error fetching plans:", error);
        }
    }, []);

    const fetchCurrentStorage = useCallback(async () => {
        if (!currentUserUid) return;
        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch(`${API_BASE}/users/${uid}/storage/details/`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `${token}`,
                    "auth-token": `Bearer ${token}`,
                    "requesting-user-uid": currentUserUid,
                },
            });
            if (response.ok) {
                const data = await response.json();
                setCurrentPlan(data.current_plan);
                setCurrentPlanQuantity(data.storage_info?.storage_plan_quantity || 1);
                setStorageUsedBytes(data.storage_info?.storage_used_bytes || 0);
            }
        } catch (error) {
            console.error("Error fetching current storage:", error);
        }
    }, [uid, currentUserUid]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await fetchPlans();
            if (isLoggedIn && currentUserUid) {
                await fetchCurrentStorage();
            }
            setLoading(false);
        };
        loadData();
    }, [isLoggedIn, currentUserUid, fetchPlans, fetchCurrentStorage]);

    const handleUpgrade = async (plan) => {
        if (!plan.stripe_price_id) return;
        setProcessingPlanId(plan.id);
        setError(null);

        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch(`${API_BASE}/users/${uid}/storage/checkout/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `${token}`,
                    "auth-token": `Bearer ${token}`,
                    "requesting-user-uid": currentUserUid,
                },
                body: JSON.stringify({
                    plan_id: plan.id,
                    quantity: plan.is_stackable ? (blockQuantities[plan.id] || 1) : 1,
                    success_url: `${window.location.origin}/account/${uid}/storage?session_id={CHECKOUT_SESSION_ID}`,
                    cancel_url: `${window.location.origin}/account/${uid}/plans`,
                }),
            });

            if (response.ok) {
                const data = await response.json();
                // Redirect to Stripe Checkout
                if (data.checkout_url) {
                    window.location.href = data.checkout_url;
                }
            } else {
                const data = await response.json();
                setError(data.error || "Failed to create checkout session");
            }
        } catch (error) {
            console.error("Error creating checkout session:", error);
            setError("An error occurred. Please try again.");
        } finally {
            setProcessingPlanId(null);
        }
    };

    const handleDowngrade = async (plan) => {
        if (!window.confirm(`Are you sure you want to downgrade to the ${plan.name} plan? This will reduce your storage limit to ${formatBytes(plan.storage_bytes)}.`)) {
            return;
        }

        setProcessingPlanId(plan.id);
        setError(null);

        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch(`${API_BASE}/users/${uid}/storage/downgrade/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `${token}`,
                    "auth-token": `Bearer ${token}`,
                    "requesting-user-uid": currentUserUid,
                },
                body: JSON.stringify({ plan_id: plan.id }),
            });

            if (response.ok) {
                alert(`Successfully changed to ${plan.name} plan!`);
                fetchCurrentStorage();
            } else {
                const data = await response.json();
                if (response.status === 409) {
                    setError(
                        `Cannot downgrade: you're using ${data.storage_used_mb} MB but the ${plan.name} plan only allows ${data.new_limit_mb} MB. ` +
                        `Please free up ${data.excess_mb} MB of storage first.`
                    );
                } else {
                    setError(data.error || "Failed to downgrade plan");
                }
            }
        } catch (error) {
            console.error("Error downgrading:", error);
            setError("An error occurred. Please try again.");
        } finally {
            setProcessingPlanId(null);
        }
    };

    const handleManageBlocks = async () => {
        setPortalLoading(true);
        setError(null);
        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch(`${API_BASE}/users/${uid}/storage/billing-portal/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `${token}`,
                    "auth-token": `Bearer ${token}`,
                    "requesting-user-uid": currentUserUid,
                },
                body: JSON.stringify({
                    return_url: `${window.location.origin}/account/${uid}/plans`,
                }),
            });

            if (response.ok) {
                const data = await response.json();
                window.location.href = data.portal_url;
            } else {
                const data = await response.json();
                setError(data.error || "Failed to open the billing portal");
            }
        } catch (error) {
            console.error("Error opening billing portal:", error);
            setError("An error occurred. Please try again.");
        } finally {
            setPortalLoading(false);
        }
    };

    const handleBlockQuantityChange = (planId, value) => {
        const quantity = Math.max(1, parseInt(value, 10) || 1);
        setBlockQuantities((prev) => ({ ...prev, [planId]: quantity }));
    };

    const formatBytes = (bytes) => {
        if (!bytes || bytes === 0) return "0 B";
        const k = 1024;
        const sizes = ["B", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    };

    const isCurrentPlan = (plan) => {
        return currentPlan && currentPlan.id === plan.id;
    };

    const isUpgrade = (plan) => {
        if (!currentPlan) return plan.price_monthly_cents > 0;
        return plan.storage_bytes > currentPlan.storage_bytes;
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="StoragePlans__loading">
                    <p>Loading storage plans...</p>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="StoragePlans__title-section">
                <h1>Storage Plans</h1>
                <p style={{ marginTop: "8px", opacity: 0.8 }}>Choose the plan that fits your documentation needs</p>
            </div>
            <div className="StoragePlans__main-content">
                <button className="StoragePlans__navigation-buttons" onClick={() => navigate(`/account/${uid}/storage`)}>
                    <i className="fas fa-arrow-left"></i> Back to Storage
                </button>

                <div className="StoragePlans__grid">
                    {plans.map((plan) => (
                        // Temporarily not offering the 10 GB "Pro" plan — revisit later.
                        // Remove this line to bring it back into the picker.
                        plan.name === "Pro" ? null :
                        <div
                            key={plan.id}
                            className={`StoragePlans__card ${isCurrentPlan(plan) ? "StoragePlans__card--current" : ""}`}
                        >
                            {isCurrentPlan(plan) && (
                                <span className="StoragePlans__current-badge">Current Plan</span>
                            )}
                            <div className="StoragePlans__card-name">{plan.name}</div>
                            <div className="StoragePlans__card-storage">
                                {plan.is_stackable && isCurrentPlan(plan)
                                    ? `${formatBytes(plan.storage_bytes * currentPlanQuantity)} (${currentPlanQuantity} × ${formatBytes(plan.storage_bytes)})`
                                    : plan.is_stackable
                                        ? `${formatBytes(plan.storage_bytes)} per block`
                                        : formatBytes(plan.storage_bytes)}
                            </div>
                            <div className="StoragePlans__card-price">{plan.price_display}</div>

                            {plan.features && plan.features.length > 0 && (
                                <ul className="StoragePlans__card-features">
                                    {plan.features.map((feature, idx) => (
                                        <li key={idx}>{feature}</li>
                                    ))}
                                </ul>
                            )}

                            {plan.description && (
                                <p style={{ fontSize: "0.85rem", color: "var(--bb-text-muted)", marginBottom: "16px" }}>
                                    {plan.description}
                                </p>
                            )}

                            {isCurrentPlan(plan) && plan.is_stackable ? (
                                <button
                                    className="StoragePlans__card-button StoragePlans__card-button--upgrade"
                                    onClick={handleManageBlocks}
                                    disabled={portalLoading}
                                >
                                    {portalLoading ? "Opening..." : "Add or Remove Blocks"}
                                </button>
                            ) : isCurrentPlan(plan) ? (
                                <button className="StoragePlans__card-button StoragePlans__card-button--current" disabled>
                                    Current Plan
                                </button>
                            ) : plan.is_stackable ? (
                                <>
                                    <input
                                        type="number"
                                        min="1"
                                        className="StoragePlans__card-quantity"
                                        value={blockQuantities[plan.id] || 1}
                                        onChange={(e) => handleBlockQuantityChange(plan.id, e.target.value)}
                                        aria-label={`Number of ${formatBytes(plan.storage_bytes)} blocks`}
                                    />
                                    <button
                                        className="StoragePlans__card-button StoragePlans__card-button--upgrade"
                                        onClick={() => handleUpgrade(plan)}
                                        disabled={processingPlanId === plan.id || !plan.stripe_price_id}
                                    >
                                        {processingPlanId === plan.id ? "Processing..." : "Subscribe"}
                                    </button>
                                </>
                            ) : isUpgrade(plan) ? (
                                <button
                                    className="StoragePlans__card-button StoragePlans__card-button--upgrade"
                                    onClick={() => handleUpgrade(plan)}
                                    disabled={processingPlanId === plan.id || !plan.stripe_price_id}
                                >
                                    {processingPlanId === plan.id ? "Processing..." : "Upgrade"}
                                </button>
                            ) : (
                                <button
                                    className="StoragePlans__card-button StoragePlans__card-button--downgrade"
                                    onClick={() => handleDowngrade(plan)}
                                    disabled={processingPlanId === plan.id}
                                >
                                    {processingPlanId === plan.id ? "Processing..." : "Downgrade"}
                                </button>
                            )}
                        </div>
                    ))}
                </div>

                {error && (
                    <div className="StoragePlans__error">
                        <strong>Error:</strong> {error}
                    </div>
                )}
            </div>
        </>
    );
}

export default StoragePlans;
